import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  LayoutDashboard, FileText, Image, Search, MessageSquare, Settings, Menu, X, Sparkles,
  TrendingUp, Clock, Send, Copy, Check, Loader2, RefreshCw, Instagram, Facebook, Twitter,
  Linkedin, Mail, BookOpen, Zap, Hash, HelpCircle, Lightbulb, Palette, PenTool, Plus,
  Trash2, ExternalLink, Activity, Calendar, Users, Target, BarChart3, Download,
  Layers, Split, FlaskConical, Megaphone, FolderOpen, ChevronRight, Play, Pause,
  Edit, Eye, Filter, ArrowUpRight, Briefcase, Globe, Video, Mic, FileCode, PieChart,
  TrendingDown, UserPlus, Star, Heart, MessageCircle, Share2, Lock, LogOut, ShieldCheck,
  AlertCircle, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== AUTH ==============

// Restore token from localStorage on load
const _storedToken = localStorage.getItem('marketai_token');
if (_storedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_storedToken}`;
}

// Auto-logout on 401
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('marketai_token');
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('marketai_token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('marketai_token');
        return null;
      }
      return payload;
    } catch {
      return null;
    }
  });

  const login = (token) => {
    localStorage.setItem('marketai_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUser(payload);
    } catch { setUser({}); }
  };

  const logout = () => {
    localStorage.removeItem('marketai_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Login / Setup Page
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsSetup, setNeedsSetup] = useState(null);
  const { login, user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/auth/check-setup`)
      .then(r => setNeedsSetup(r.data.needs_setup))
      .catch(() => setNeedsSetup(false));
  }, []);

  if (user) return <Navigate to="/" replace />;

  if (needsSetup === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const endpoint = needsSetup ? '/auth/setup' : '/auth/login';
      const res = await axios.post(`${API}${endpoint}`, { username, password });
      login(res.data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-primary/30 mb-4">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">MarketAI Pro</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {needsSetup ? 'Configura el primer administrador' : 'Inicia sesión para continuar'}
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            {needsSetup && (
              <div className="flex items-center gap-2 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg text-sm text-violet-400 mb-4">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span>No hay usuarios configurados. Crea el primer administrador.</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Usuario</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Nombre de usuario"
                  autoComplete="username"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={needsSetup ? 'new-password' : 'current-password'}
                  required
                />
              </div>
              {error && (
                <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : <Lock className="w-4 h-4 mr-2" />
                }
                {needsSetup ? 'Crear administrador' : 'Iniciar sesión'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  
  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/content", icon: FileText, label: "Contenido" },
    { path: "/ab-testing", icon: FlaskConical, label: "A/B Testing" },
    { path: "/repurpose", icon: Layers, label: "Repurpose" },
    { path: "/hashtags", icon: Hash, label: "Hashtags" },
    { path: "/images", icon: Image, label: "Imagenes" },
    { path: "/seo", icon: Search, label: "SEO" },
    { path: "/social-publisher", icon: Share2, label: "Publicar" },
    { path: "/calendar", icon: Calendar, label: "Calendario" },
    { path: "/templates", icon: FolderOpen, label: "Templates" },
    { path: "/leads", icon: Users, label: "Leads" },
    { path: "/campaigns", icon: Megaphone, label: "Campanas" },
    { path: "/email-sequences", icon: Mail, label: "Email Seq." },
    { path: "/auto-responses", icon: MessageSquare, label: "Respuestas" },
    { path: "/response-forms", icon: FileText, label: "Formularios" },
    { path: "/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/settings", icon: Settings, label: "Config" },
  ];

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />
      )}
      
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-card/80 backdrop-blur-xl border-r border-border transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center justify-between px-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center animate-pulse-glow">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-bold text-lg font-['Plus_Jakarta_Sans']">MarketAI</span>
              <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">PRO</Badge>
            </div>
          </div>
          <button className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors" onClick={() => setIsOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <ScrollArea className="h-[calc(100vh-8rem)]">
          <nav className="p-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${isActive ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              >
                <item.icon className="w-4 h-4" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </ScrollArea>
        
        <div className="absolute bottom-4 left-4 right-4">
          <Card className="bg-gradient-to-br from-violet-600/10 to-indigo-600/10 border-primary/20">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-muted-foreground">Chatwoot</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 truncate">chat.experimentia.es</p>
            </CardContent>
          </Card>
        </div>
      </aside>
    </>
  );
};

// Header Component
const Header = ({ setIsOpen }) => {
  const { user, logout } = useAuth();
  return (
    <header className="sticky top-0 z-30 h-16 bg-background/80 backdrop-blur-md border-b border-border flex items-center justify-between px-6 lg:pl-72">
      <button className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors" onClick={() => setIsOpen(true)}>
        <Menu className="w-5 h-5" />
      </button>
      <div className="flex items-center gap-3 ml-auto">
        <Badge variant="outline" className="gap-2 bg-green-500/10 text-green-500 border-green-500/30">
          <Zap className="w-3 h-3" />
          GLM-5 Activo
        </Badge>
        {user && (
          <>
            <span className="text-sm text-muted-foreground hidden sm:block font-medium">{user.username}</span>
            <Button variant="ghost" size="icon" onClick={logout} title="Cerrar sesión" className="text-muted-foreground hover:text-foreground">
              <LogOut className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </header>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, trend, color, onClick }) => (
  <Card className={`relative overflow-hidden hover:border-primary/50 transition-all cursor-pointer group`} onClick={onClick} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-5 group-hover:opacity-10 transition-opacity`} />
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-xs ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              <span>{Math.abs(trend)}% vs semana pasada</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Dashboard Page
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Error al cargar estadisticas");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    { label: "Contenido Generado", value: stats?.total_content_generated || 0, icon: FileText, color: "from-violet-600 to-indigo-600", trend: 12 },
    { label: "Analisis SEO", value: stats?.total_seo_analyses || 0, icon: Search, color: "from-emerald-600 to-teal-600", trend: 8 },
    { label: "Auto-Respuestas", value: stats?.total_auto_responses || 0, icon: MessageSquare, color: "from-orange-600 to-amber-600", trend: -3 },
    { label: "Leads Totales", value: stats?.total_leads || 0, icon: Users, color: "from-pink-600 to-rose-600", trend: 25 },
    { label: "Campanas Activas", value: stats?.total_campaigns || 0, icon: Megaphone, color: "from-cyan-600 to-blue-600", trend: 0 },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">Centro de comando de tu marketing con IA</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Actividad Semanal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-48">
              {stats?.weekly_stats?.map((day, index) => (
                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                  <div 
                    className="w-full bg-gradient-to-t from-violet-600 to-indigo-600 rounded-t-md transition-all hover:opacity-80"
                    style={{ height: `${Math.max(day.content_count * 30, 20)}px` }}
                  />
                  <span className="text-xs text-muted-foreground">{day.day}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-primary" />
              Por Plataforma
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.content_by_platform || {}).length > 0 ? (
                Object.entries(stats.content_by_platform).map(([platform, count]) => (
                  <div key={platform} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {platform === 'instagram' && <Instagram className="w-4 h-4 text-pink-500" />}
                      {platform === 'facebook' && <Facebook className="w-4 h-4 text-blue-500" />}
                      {platform === 'twitter' && <Twitter className="w-4 h-4 text-sky-500" />}
                      {platform === 'linkedin' && <Linkedin className="w-4 h-4 text-blue-700" />}
                      <span className="capitalize text-sm">{platform}</span>
                    </div>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-8 text-sm">Sin datos aun</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Leads por Estado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.leads_by_status || {}).length > 0 ? (
                Object.entries(stats.leads_by_status).map(([status, count]) => (
                  <div key={status} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="capitalize">{status}</span>
                      <span>{count}</span>
                    </div>
                    <Progress value={(count / stats.total_leads) * 100} className="h-2" />
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-8 text-sm">Sin leads aun</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Actividad Reciente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              {stats?.recent_activity?.length > 0 ? (
                <div className="space-y-2">
                  {stats.recent_activity.slice(0, 6).map((activity, index) => (
                    <div key={index} className="flex items-start gap-3 p-2 bg-muted/30 rounded-lg">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        activity.type === 'content' ? 'bg-violet-600/20 text-violet-500' :
                        activity.type === 'seo' ? 'bg-emerald-600/20 text-emerald-500' :
                        'bg-orange-600/20 text-orange-500'
                      }`}>
                        {activity.type === 'content' ? <FileText className="w-4 h-4" /> :
                         activity.type === 'seo' ? <Search className="w-4 h-4" /> :
                         <MessageSquare className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {activity.data?.topic || activity.data?.customer_message || 'Actividad'}
                        </p>
                        <p className="text-xs text-muted-foreground">{activity.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">Sin actividad reciente</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Content Generator Page
const ContentGenerator = () => {
  const [contentType, setContentType] = useState("social_media");
  const [platform, setPlatform] = useState("instagram");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [context, setContext] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const platforms = [
    { value: "instagram", label: "Instagram", icon: Instagram },
    { value: "facebook", label: "Facebook", icon: Facebook },
    { value: "twitter", label: "Twitter/X", icon: Twitter },
    { value: "linkedin", label: "LinkedIn", icon: Linkedin },
  ];

  const contentTypes = [
    { value: "social_media", label: "Redes Sociales", icon: Instagram },
    { value: "email", label: "Email", icon: Mail },
    { value: "blog", label: "Blog", icon: BookOpen },
    { value: "ad_copy", label: "Anuncios", icon: Megaphone },
    { value: "landing_page", label: "Landing Page", icon: Globe },
  ];

  const tones = [
    { value: "professional", label: "Profesional" },
    { value: "casual", label: "Casual" },
    { value: "humorous", label: "Humoristico" },
    { value: "formal", label: "Formal" },
    { value: "inspirational", label: "Inspiracional" },
    { value: "urgent", label: "Urgente" },
    { value: "friendly", label: "Amigable" },
  ];

  const generateContent = async () => {
    if (!topic.trim()) {
      toast.error("Por favor ingresa un tema");
      return;
    }

    setLoading(true);
    setGeneratedContent("");

    try {
      const response = await axios.post(`${API}/content/generate`, {
        content_type: contentType,
        platform: contentType === "social_media" || contentType === "ad_copy" ? platform : null,
        topic,
        tone,
        language: "es",
        additional_context: context || null,
        brand_voice: brandVoice || null,
        target_audience: targetAudience || null
      });
      
      setGeneratedContent(response.data.generated_content);
      toast.success("Contenido generado exitosamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar contenido");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-generator-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Generador de Contenido</h1>
        <p className="text-muted-foreground mt-2">Crea contenido de marketing profesional con IA</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {contentTypes.map((type) => (
          <Button
            key={type.value}
            variant={contentType === type.value ? "default" : "outline"}
            className={`gap-2 ${contentType === type.value ? 'btn-gradient' : ''}`}
            onClick={() => setContentType(type.value)}
          >
            <type.icon className="w-4 h-4" />
            {type.label}
          </Button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Configuracion</CardTitle>
            <CardDescription>Define los parametros para tu contenido</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(contentType === "social_media" || contentType === "ad_copy") && (
              <div className="space-y-2">
                <Label>Plataforma</Label>
                <div className="grid grid-cols-2 gap-2">
                  {platforms.map((p) => (
                    <Button
                      key={p.value}
                      variant={platform === p.value ? "default" : "outline"}
                      size="sm"
                      className={`justify-start gap-2 ${platform === p.value ? 'btn-gradient' : ''}`}
                      onClick={() => setPlatform(p.value)}
                    >
                      <p.icon className="w-4 h-4" />
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="topic">Tema *</Label>
              <Input
                id="topic"
                placeholder="Ej: Lanzamiento de nuevo producto de skincare organico..."
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tono</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {tones.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Audiencia objetivo</Label>
                <Input
                  placeholder="Ej: Mujeres 25-45 anos"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Voz de marca (opcional)</Label>
              <Input
                placeholder="Ej: Amigable, experto, innovador"
                value={brandVoice}
                onChange={(e) => setBrandVoice(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Contexto adicional (opcional)</Label>
              <Textarea
                placeholder="Informacion adicional sobre tu marca, producto, promocion..."
                value={context}
                onChange={(e) => setContext(e.target.value)}
                rows={3}
              />
            </div>

            <Button className="w-full btn-gradient" onClick={generateContent} disabled={loading}>
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Generar Contenido</>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-muted/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Resultado</CardTitle>
              {generatedContent && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyToClipboard}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={generateContent} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {generatedContent ? (
              <div className="ai-output min-h-[400px] whitespace-pre-wrap">{generatedContent}</div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-muted-foreground">
                <Sparkles className="w-12 h-12 mb-4 opacity-50" />
                <p>El contenido generado aparecera aqui</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// A/B Testing Page
const ABTestingPage = () => {
  const [topic, setTopic] = useState("");
  const [contentType, setContentType] = useState("social_media");
  const [platform, setPlatform] = useState("instagram");
  const [variations, setVariations] = useState(3);
  const [tone, setTone] = useState("professional");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateABTest = async () => {
    if (!topic.trim()) {
      toast.error("Por favor ingresa un tema");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await axios.post(`${API}/content/ab-test`, {
        topic,
        content_type: contentType,
        platform,
        variations,
        tone
      });
      setResult(response.data);
      toast.success("Variaciones generadas");
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al generar variaciones";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ab-testing-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">A/B Testing Generator</h1>
        <p className="text-muted-foreground mt-2">Genera variaciones para testear cual convierte mejor</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-primary" />
              Configuracion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Tema</Label>
              <Input placeholder="Ej: Promocion Black Friday" value={topic} onChange={(e) => setTopic(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Tipo de contenido</Label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="social_media">Redes Sociales</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="ad_copy">Anuncios</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Numero de variaciones: {variations}</Label>
              <input
                type="range"
                min="2"
                max="5"
                value={variations}
                onChange={(e) => setVariations(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            <Button className="w-full btn-gradient" onClick={generateABTest} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Split className="w-4 h-4 mr-2" />}
              Generar {variations} Variaciones
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 bg-muted/20">
          <CardHeader>
            <CardTitle>Variaciones A/B</CardTitle>
            {result && <Badge>{result.variations_count} variaciones</Badge>}
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <div className="ai-output whitespace-pre-wrap">{result.variations}</div>
                <div className="p-3 bg-primary/10 rounded-lg">
                  <p className="text-sm text-primary flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    {result.recommendation}
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] text-destructive">
                <AlertCircle className="w-12 h-12 mb-4 opacity-70" />
                <p className="font-medium">Error al generar variaciones</p>
                <p className="text-sm text-muted-foreground mt-2 text-center max-w-md">{error}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[300px] text-muted-foreground">
                <Split className="w-12 h-12 mb-4 opacity-50" />
                <p>Las variaciones apareceran aqui</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Repurpose Content Page
const RepurposePage = () => {
  const [originalContent, setOriginalContent] = useState("");
  const [sourceFormat, setSourceFormat] = useState("blog");
  const [targetFormats, setTargetFormats] = useState(["twitter", "linkedin"]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const formats = [
    { value: "twitter", label: "Twitter/X", icon: Twitter },
    { value: "linkedin", label: "LinkedIn", icon: Linkedin },
    { value: "instagram", label: "Instagram", icon: Instagram },
    { value: "facebook", label: "Facebook", icon: Facebook },
    { value: "email", label: "Email", icon: Mail },
    { value: "tiktok", label: "TikTok", icon: Video },
    { value: "youtube", label: "YouTube", icon: Play },
    { value: "podcast", label: "Podcast", icon: Mic },
    { value: "infographic", label: "Infografia", icon: PieChart },
  ];

  const toggleFormat = (format) => {
    setTargetFormats(prev => 
      prev.includes(format) 
        ? prev.filter(f => f !== format)
        : [...prev, format]
    );
  };

  const repurposeContent = async () => {
    if (!originalContent.trim() || targetFormats.length === 0) {
      toast.error("Ingresa contenido y selecciona formatos");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/content/repurpose`, {
        original_content: originalContent,
        source_format: sourceFormat,
        target_formats: targetFormats
      });
      setResult(response.data);
      toast.success(`${response.data.formats_generated} formatos generados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al repurpose");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="repurpose-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Repurpose Content</h1>
        <p className="text-muted-foreground mt-2">Convierte un contenido en multiples formatos</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              Contenido Original
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Formato original</Label>
              <Select value={sourceFormat} onValueChange={setSourceFormat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blog">Blog</SelectItem>
                  <SelectItem value="social">Social Media</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="video">Video Script</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Contenido</Label>
              <Textarea
                placeholder="Pega aqui tu contenido original..."
                value={originalContent}
                onChange={(e) => setOriginalContent(e.target.value)}
                rows={8}
              />
            </div>
            <div className="space-y-2">
              <Label>Convertir a:</Label>
              <div className="flex flex-wrap gap-2">
                {formats.map((f) => (
                  <Button
                    key={f.value}
                    variant={targetFormats.includes(f.value) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleFormat(f.value)}
                    className="gap-1"
                  >
                    <f.icon className="w-3 h-3" />
                    {f.label}
                  </Button>
                ))}
              </div>
            </div>
            <Button className="w-full btn-gradient" onClick={repurposeContent} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Repurpose a {targetFormats.length} formatos
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-muted/20">
          <CardHeader>
            <CardTitle>Contenido Adaptado</CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {Object.entries(result.repurposed_content).map(([format, content]) => (
                    <div key={format} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="capitalize">{format}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => {
                          navigator.clipboard.writeText(content);
                          toast.success("Copiado");
                        }}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="ai-output text-sm">{content}</div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-muted-foreground">
                <Layers className="w-12 h-12 mb-4 opacity-50" />
                <p>El contenido adaptado aparecera aqui</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Hashtag Generator Page
const HashtagsPage = () => {
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [count, setCount] = useState(15);
  const [includeTrending, setIncludeTrending] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateHashtags = async () => {
    if (!topic.trim()) {
      toast.error("Por favor ingresa un tema");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/content/hashtags`, {
        topic,
        platform,
        count,
        include_trending: includeTrending
      });
      setResult(response.data);
      toast.success("Hashtags generados");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="hashtags-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Generador de Hashtags</h1>
        <p className="text-muted-foreground mt-2">Hashtags optimizados para cada plataforma</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-primary" />
              Configuracion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Tema</Label>
              <Input placeholder="Ej: Marketing digital" value={topic} onChange={(e) => setTopic(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Plataforma</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="twitter">Twitter/X</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cantidad: {count}</Label>
              <input type="range" min="5" max="30" value={count} onChange={(e) => setCount(parseInt(e.target.value))} className="w-full" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Incluir trending</Label>
              <Switch checked={includeTrending} onCheckedChange={setIncludeTrending} />
            </div>
            <Button className="w-full btn-gradient" onClick={generateHashtags} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Hash className="w-4 h-4 mr-2" />}
              Generar Hashtags
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 bg-muted/20">
          <CardHeader>
            <CardTitle>Hashtags Optimizados</CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <div className="ai-output whitespace-pre-wrap">{JSON.stringify(result.hashtags, null, 2)}</div>
                <div className="p-3 bg-primary/10 rounded-lg">
                  <p className="text-sm text-primary flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    {result.best_practices}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[300px] text-muted-foreground">
                <Hash className="w-12 h-12 mb-4 opacity-50" />
                <p>Los hashtags apareceran aqui</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Image Generator Page
const ImageGenerator = () => {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("modern");
  const [imageType, setImageType] = useState("logo");
  const [brandName, setBrandName] = useState("");
  const [colors, setColors] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const styles = [
    { value: "modern", label: "Moderno", icon: Sparkles },
    { value: "minimalist", label: "Minimalista", icon: PenTool },
    { value: "vintage", label: "Vintage", icon: Palette },
    { value: "corporate", label: "Corporativo", icon: Briefcase },
    { value: "playful", label: "Divertido", icon: Heart },
    { value: "luxury", label: "Lujo", icon: Star },
  ];

  const generateImage = async () => {
    if (!prompt.trim()) {
      toast.error("Por favor describe tu imagen");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/image/generate`, {
        prompt,
        style,
        type: imageType,
        brand_name: brandName || null,
        colors: colors ? colors.split(',').map(c => c.trim()) : null
      });
      setResult(response.data);
      toast.success("Brief de diseno generado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="image-generator-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Generador de Imagenes</h1>
        <p className="text-muted-foreground mt-2">Crea briefs detallados para logos y graficos</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Configuracion</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de imagen</Label>
              <Select value={imageType} onValueChange={setImageType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="logo">Logo</SelectItem>
                  <SelectItem value="banner">Banner</SelectItem>
                  <SelectItem value="social_post">Post Social</SelectItem>
                  <SelectItem value="icon">Icono</SelectItem>
                  <SelectItem value="illustration">Ilustracion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estilo</Label>
              <div className="grid grid-cols-3 gap-2">
                {styles.map((s) => (
                  <Button
                    key={s.value}
                    variant={style === s.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStyle(s.value)}
                    className="gap-1"
                  >
                    <s.icon className="w-3 h-3" />{s.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre de marca (opcional)</Label>
              <Input placeholder="Ej: TechFlow" value={brandName} onChange={(e) => setBrandName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Colores (separados por coma)</Label>
              <Input placeholder="Ej: azul, blanco, dorado" value={colors} onChange={(e) => setColors(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Descripcion del concepto *</Label>
              <Textarea placeholder="Ej: Logo para startup de fintech que transmita confianza e innovacion..." value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} />
            </div>
            <Button className="w-full btn-gradient" onClick={generateImage} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
              Generar Brief de Diseno
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-muted/20">
          <CardHeader><CardTitle>Brief de Diseno</CardTitle></CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Badge>{result.type}</Badge>
                  <Badge variant="outline">{result.style}</Badge>
                </div>
                <div className="ai-output min-h-[350px] whitespace-pre-wrap">{result.design_brief}</div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-muted-foreground">
                <Image className="w-12 h-12 mb-4 opacity-50" />
                <p>El brief de diseno aparecera aqui</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// SEO Assistant Page
const SEOAssistant = () => {
  const [topic, setTopic] = useState("");
  const [url, setUrl] = useState("");
  const [keywords, setKeywords] = useState("");
  const [competitors, setCompetitors] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyzeSEO = async () => {
    if (!topic.trim()) {
      toast.error("Por favor ingresa un tema");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/seo/analyze`, {
        topic,
        url: url || null,
        target_keywords: keywords ? keywords.split(',').map(k => k.trim()) : null,
        competitor_urls: competitors ? competitors.split(',').map(c => c.trim()) : null,
        language: "es"
      });
      setResult(response.data);
      toast.success("Analisis SEO completado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en analisis SEO");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="seo-assistant-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Asistente SEO</h1>
        <p className="text-muted-foreground mt-2">Analisis SEO avanzado con insights de competencia</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Analisis</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Tema o Keyword principal *</Label>
              <Input placeholder="Ej: Marketing digital para pymes" value={topic} onChange={(e) => setTopic(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>URL a optimizar (opcional)</Label>
              <Input placeholder="https://tusitio.com/pagina" value={url} onChange={(e) => setUrl(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Keywords objetivo (separadas por coma)</Label>
              <Input placeholder="seo, marketing, posicionamiento web" value={keywords} onChange={(e) => setKeywords(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>URLs de competidores (opcional)</Label>
              <Textarea placeholder="https://competidor1.com, https://competidor2.com" value={competitors} onChange={(e) => setCompetitors(e.target.value)} rows={2} />
            </div>
            <Button className="w-full btn-gradient" onClick={analyzeSEO} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Analizar SEO
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {result ? (
            <>
              <Card>
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Hash className="w-5 h-5 text-primary" />Meta Tags</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-muted-foreground">Titulo ({result.meta_title?.length || 0}/60)</Label>
                    <p className="font-mono text-sm bg-muted/50 p-2 rounded mt-1">{result.meta_title}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Descripcion ({result.meta_description?.length || 0}/160)</Label>
                    <p className="font-mono text-sm bg-muted/50 p-2 rounded mt-1">{result.meta_description}</p>
                  </div>
                  {result.readability_score && (
                    <div className="flex items-center gap-2">
                      <Label className="text-muted-foreground">Legibilidad:</Label>
                      <Badge className={result.readability_score > 70 ? 'bg-green-500' : result.readability_score > 50 ? 'bg-yellow-500' : 'bg-red-500'}>{result.readability_score}/100</Badge>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Zap className="w-5 h-5 text-primary" />Keywords</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">{result.keywords?.map((kw, i) => (<Badge key={i} variant="secondary">{kw}</Badge>))}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><HelpCircle className="w-5 h-5 text-primary" />FAQs</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {result.faq_suggestions?.map((faq, i) => (
                      <div key={i} className="bg-muted/30 p-3 rounded-lg">
                        <p className="font-medium text-sm">{faq.question}</p>
                        <p className="text-muted-foreground text-sm mt-1">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Lightbulb className="w-5 h-5 text-primary" />Tips SEO</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {result.seo_tips?.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500 mt-0.5" />{tip}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-muted/20">
              <CardContent className="flex flex-col items-center justify-center min-h-[500px] text-muted-foreground">
                <Search className="w-12 h-12 mb-4 opacity-50" />
                <p>Los resultados del analisis apareceran aqui</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Calendar Page
const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bestTimes, setBestTimes] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState("instagram");

  useEffect(() => {
    fetchEvents();
    fetchBestTimes("instagram");
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar/events`);
      setEvents(response.data.events || []);
    } catch (error) {
      toast.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  };

  const fetchBestTimes = async (platform) => {
    try {
      const response = await axios.get(`${API}/calendar/best-times?platform=${platform}`);
      setBestTimes(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Calendario de Contenido</h1>
        <p className="text-muted-foreground mt-2">Planifica y programa tu contenido</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Eventos Programados
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : events.length > 0 ? (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {events.map((event) => (
                    <div key={event.id} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        event.status === 'published' ? 'bg-green-600/20 text-green-500' :
                        event.status === 'scheduled' ? 'bg-blue-600/20 text-blue-500' :
                        'bg-gray-600/20 text-gray-500'
                      }`}>
                        {event.status === 'published' ? <Check className="w-5 h-5" /> :
                         event.status === 'scheduled' ? <Clock className="w-5 h-5" /> :
                         <Edit className="w-5 h-5" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{event.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{event.platform}</Badge>
                          <span className="text-xs text-muted-foreground">{new Date(event.scheduled_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Badge className={event.status === 'published' ? 'bg-green-500' : event.status === 'scheduled' ? 'bg-blue-500' : 'bg-gray-500'}>{event.status}</Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay eventos programados</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Mejores Horarios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={selectedPlatform} onValueChange={(v) => { setSelectedPlatform(v); fetchBestTimes(v); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="twitter">Twitter/X</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
              </SelectContent>
            </Select>
            
            {bestTimes && (
              <div className="space-y-3">
                <div>
                  <Label className="text-muted-foreground">Mejores dias</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {bestTimes.best_days?.map((day, i) => (<Badge key={i} variant="secondary">{day}</Badge>))}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Mejores horas</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {bestTimes.best_hours?.map((hour, i) => (<Badge key={i}>{hour}</Badge>))}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Pico de engagement</Label>
                  <p className="text-sm mt-1 text-primary font-medium">{bestTimes.engagement_peak}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Evitar</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {bestTimes.avoid?.map((time, i) => (<Badge key={i} variant="destructive" className="text-xs">{time}</Badge>))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Templates Page
const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState({});
  const [filledContent, setFilledContent] = useState("");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error("Error al cargar templates");
    } finally {
      setLoading(false);
    }
  };

  const useTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const response = await axios.post(`${API}/templates/${selectedTemplate.id}/use`, variables);
      setFilledContent(response.data.filled_content);
      toast.success("Template aplicado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aplicar template");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Templates</h1>
        <p className="text-muted-foreground mt-2">Plantillas profesionales listas para usar</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Biblioteca de Templates</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${selectedTemplate?.id === template.id ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
                    onClick={() => { setSelectedTemplate(template); setVariables({}); setFilledContent(""); }}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{template.name}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{template.category}</Badge>
                          <Badge variant="secondary" className="text-xs">{template.content_type}</Badge>
                        </div>
                      </div>
                      {template.is_premium && <Star className="w-4 h-4 text-yellow-500" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{template.template_content}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Usar Template</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {selectedTemplate ? (
              <>
                <p className="text-sm text-muted-foreground">Completa las variables:</p>
                {selectedTemplate.variables?.map((variable) => (
                  <div key={variable} className="space-y-1">
                    <Label className="capitalize">{variable.replace(/_/g, ' ')}</Label>
                    <Input
                      placeholder={`Ingresa ${variable}`}
                      value={variables[variable] || ""}
                      onChange={(e) => setVariables({...variables, [variable]: e.target.value})}
                    />
                  </div>
                ))}
                <Button className="w-full btn-gradient" onClick={useTemplate}>
                  <FileCode className="w-4 h-4 mr-2" />
                  Aplicar Template
                </Button>
                {filledContent && (
                  <div className="ai-output text-sm mt-4">{filledContent}</div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Selecciona un template</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Leads Page
const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [newInteraction, setNewInteraction] = useState({ type: "note", content: "" });
  const [addingInteraction, setAddingInteraction] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [filter]);

  const fetchLeads = async () => {
    try {
      const url = filter && filter !== "all" ? `${API}/leads?status=${filter}` : `${API}/leads`;
      const response = await axios.get(url);
      setLeads(response.data.leads || []);
    } catch (error) {
      toast.error("Error al cargar leads");
    } finally {
      setLoading(false);
    }
  };

  const addInteraction = async () => {
    if (!newInteraction.content.trim() || !selectedLead) return;
    setAddingInteraction(true);
    try {
      const res = await axios.post(`${API}/leads/${selectedLead.id}/interaction`, {
        type: newInteraction.type,
        content: newInteraction.content
      });
      toast.success("Interaccion agregada");
      setNewInteraction({ type: "note", content: "" });
      // Update lead in local state
      setSelectedLead(prev => ({
        ...prev,
        score: res.data.new_score,
        interactions: [...(prev.interactions || []), { ...newInteraction, timestamp: new Date().toISOString() }]
      }));
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar interaccion");
    } finally {
      setAddingInteraction(false);
    }
  };

  const updateLeadStatus = async (newStatus) => {
    if (!selectedLead) return;
    try {
      await axios.put(`${API}/leads/${selectedLead.id}`, { status: newStatus });
      toast.success("Estado actualizado");
      setSelectedLead(prev => ({ ...prev, status: newStatus }));
      fetchLeads();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Leads</h1>
          <p className="text-muted-foreground mt-2">Gestiona y puntua tus leads</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Filtrar" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="new">Nuevos</SelectItem>
            <SelectItem value="contacted">Contactados</SelectItem>
            <SelectItem value="qualified">Calificados</SelectItem>
            <SelectItem value="converted">Convertidos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : leads.length > 0 ? (
            <div className="divide-y divide-border">
              {leads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-4 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setSelectedLead(lead)}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{lead.name || lead.email || 'Sin nombre'}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Badge variant="outline" className="text-xs">{lead.source}</Badge>
                        <span>{lead.interactions?.length || 0} interacciones</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${getScoreColor(lead.score)}`}>{lead.score}</p>
                      <p className="text-xs text-muted-foreground">Score</p>
                    </div>
                    <Badge className={
                      lead.status === 'converted' ? 'bg-green-500' :
                      lead.status === 'qualified' ? 'bg-blue-500' :
                      lead.status === 'contacted' ? 'bg-yellow-500' :
                      'bg-gray-500'
                    }>{lead.status}</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay leads</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selectedLead} onOpenChange={(open) => { if (!open) setSelectedLead(null); }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedLead?.name || selectedLead?.email || 'Lead'}</SheetTitle>
          </SheetHeader>
          {selectedLead && (
            <div className="space-y-6 mt-6">
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <p className={`text-4xl font-bold ${getScoreColor(selectedLead.score)}`}>{selectedLead.score}</p>
                  <p className="text-xs text-muted-foreground">Score</p>
                </div>
                <Select value={selectedLead.status} onValueChange={updateLeadStatus}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Nuevo</SelectItem>
                    <SelectItem value="contacted">Contactado</SelectItem>
                    <SelectItem value="qualified">Calificado</SelectItem>
                    <SelectItem value="converted">Convertido</SelectItem>
                    <SelectItem value="lost">Perdido</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-3">
                {selectedLead.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedLead.email}</span>
                  </div>
                )}
                {selectedLead.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedLead.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <Badge variant="outline">{selectedLead.source}</Badge>
                </div>
                {selectedLead.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedLead.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              <div>
                <p className="font-medium mb-3">Interacciones ({selectedLead.interactions?.length || 0})</p>
                <ScrollArea className="h-48">
                  <div className="space-y-2">
                    {selectedLead.interactions?.length > 0 ? selectedLead.interactions.map((inter, i) => (
                      <div key={i} className="p-2 bg-muted/30 rounded-lg text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">{inter.type}</Badge>
                          {inter.timestamp && <span className="text-xs text-muted-foreground">{new Date(inter.timestamp).toLocaleDateString()}</span>}
                        </div>
                        <p className="text-muted-foreground">{inter.content || inter.message || JSON.stringify(inter)}</p>
                      </div>
                    )) : (
                      <p className="text-sm text-muted-foreground">Sin interacciones</p>
                    )}
                  </div>
                </ScrollArea>
              </div>

              <Separator />

              <div className="space-y-3">
                <p className="font-medium">Agregar Interaccion</p>
                <Select value={newInteraction.type} onValueChange={(v) => setNewInteraction({...newInteraction, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Nota</SelectItem>
                    <SelectItem value="call">Llamada</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Reunion</SelectItem>
                    <SelectItem value="message">Mensaje</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea
                  placeholder="Detalle de la interaccion..."
                  value={newInteraction.content}
                  onChange={(e) => setNewInteraction({...newInteraction, content: e.target.value})}
                  rows={2}
                />
                <Button className="w-full" onClick={addInteraction} disabled={addingInteraction || !newInteraction.content.trim()}>
                  {addingInteraction ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Agregar
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// Campaigns Page
const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: "", description: "", platforms: [], status: "draft"
  });

  const availablePlatforms = ["instagram", "facebook", "twitter", "linkedin", "tiktok", "email"];

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      toast.error("Error al cargar campanas");
    } finally {
      setLoading(false);
    }
  };

  const togglePlatform = (platform) => {
    setNewCampaign(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const createCampaign = async () => {
    if (!newCampaign.name.trim() || !newCampaign.description.trim()) {
      toast.error("Nombre y descripcion son requeridos");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/campaigns`, newCampaign);
      toast.success("Campana creada");
      setShowCreate(false);
      setNewCampaign({ name: "", description: "", platforms: [], status: "draft" });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear campana");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Campanas</h1>
          <p className="text-muted-foreground mt-2">Gestiona tus campanas de marketing</p>
        </div>
        <Button className="btn-gradient" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4 mr-2" />Nueva Campana</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : campaigns.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{campaign.name}</CardTitle>
                  <Badge className={
                    campaign.status === 'active' ? 'bg-green-500' :
                    campaign.status === 'paused' ? 'bg-yellow-500' :
                    campaign.status === 'completed' ? 'bg-blue-500' :
                    'bg-gray-500'
                  }>{campaign.status}</Badge>
                </div>
                <CardDescription>{campaign.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1">
                  {campaign.platforms?.map((p, i) => (<Badge key={i} variant="outline" className="text-xs">{p}</Badge>))}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <span className="text-sm text-muted-foreground">{campaign.content_ids?.length || 0} contenidos</span>
                <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Megaphone className="w-12 h-12 mb-4 opacity-50" />
            <p>No hay campanas creadas</p>
            <Button className="mt-4" variant="outline" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4 mr-2" />Crear primera campana</Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Campana</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input placeholder="Ej: Black Friday 2026" value={newCampaign.name} onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <Textarea placeholder="Describe el objetivo de la campana..." value={newCampaign.description} onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Plataformas</Label>
              <div className="flex flex-wrap gap-2">
                {availablePlatforms.map((p) => (
                  <Badge
                    key={p}
                    variant={newCampaign.platforms.includes(p) ? "default" : "outline"}
                    className="cursor-pointer capitalize"
                    onClick={() => togglePlatform(p)}
                  >{p}</Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estado inicial</Label>
              <Select value={newCampaign.status} onValueChange={(v) => setNewCampaign({...newCampaign, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="active">Activa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button className="btn-gradient" onClick={createCampaign} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Crear Campana
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Email Sequences Page
const EmailSequencesPage = () => {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [genTopic, setGenTopic] = useState("");
  const [genType, setGenType] = useState("welcome");
  const [genCount, setGenCount] = useState(5);
  const [generatedSequence, setGeneratedSequence] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchSequences();
  }, []);

  const fetchSequences = async () => {
    try {
      const response = await axios.get(`${API}/email-sequences`);
      setSequences(response.data.sequences || []);
    } catch (error) {
      toast.error("Error al cargar secuencias");
    } finally {
      setLoading(false);
    }
  };

  const generateSequence = async () => {
    if (!genTopic.trim()) {
      toast.error("Ingresa un tema");
      return;
    }
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/email-sequences/generate`, null, {
        params: { topic: genTopic, sequence_type: genType, email_count: genCount }
      });
      setGeneratedSequence(response.data);
      toast.success("Secuencia generada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-sequences-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Email Sequences</h1>
          <p className="text-muted-foreground mt-2">Automatiza tus secuencias de email</p>
        </div>
        <Dialog open={generatorOpen} onOpenChange={setGeneratorOpen}>
          <DialogTrigger asChild>
            <Button className="btn-gradient"><Sparkles className="w-4 h-4 mr-2" />Generar con IA</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Generar Secuencia de Emails</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tema/Producto</Label>
                <Input placeholder="Ej: Curso de marketing digital" value={genTopic} onChange={(e) => setGenTopic(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de secuencia</Label>
                  <Select value={genType} onValueChange={setGenType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="welcome">Bienvenida</SelectItem>
                      <SelectItem value="nurture">Nurturing</SelectItem>
                      <SelectItem value="sales">Ventas</SelectItem>
                      <SelectItem value="reactivation">Reactivacion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Numero de emails: {genCount}</Label>
                  <input type="range" min="3" max="10" value={genCount} onChange={(e) => setGenCount(parseInt(e.target.value))} className="w-full" />
                </div>
              </div>
              <Button className="w-full btn-gradient" onClick={generateSequence} disabled={generating}>
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Generar {genCount} Emails
              </Button>
              {generatedSequence && (
                <ScrollArea className="h-64 mt-4">
                  <div className="ai-output text-sm">{generatedSequence.sequence}</div>
                </ScrollArea>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : sequences.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {sequences.map((seq) => (
            <Card key={seq.id} className="hover:border-primary/50">
              <CardHeader>
                <div className="flex justify-between">
                  <CardTitle className="text-lg">{seq.name}</CardTitle>
                  <Badge className={seq.is_active ? 'bg-green-500' : 'bg-gray-500'}>{seq.is_active ? 'Activa' : 'Inactiva'}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold">{seq.emails?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Emails</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{seq.subscribers || 0}</p>
                    <p className="text-xs text-muted-foreground">Suscriptores</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{(seq.open_rate * 100).toFixed(0)}%</p>
                    <p className="text-xs text-muted-foreground">Open Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Mail className="w-12 h-12 mb-4 opacity-50" />
            <p>No hay secuencias creadas</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Auto Responses Page
const AutoResponses = () => {
  const [message, setMessage] = useState("");
  const [generatedResponse, setGeneratedResponse] = useState("");
  const [sentiment, setSentiment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [newConfig, setNewConfig] = useState({ name: "", keywords: "", template: "", priority: 0 });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API}/auto-response/configs`);
      setConfigs(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const generateResponse = async () => {
    if (!message.trim()) {
      toast.error("Por favor ingresa un mensaje");
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedResponse("");
    setSentiment(null);
    try {
      const response = await axios.post(`${API}/auto-response/generate`, {
        customer_message: message,
        tone: "helpful"
      });
      setGeneratedResponse(response.data.generated_response);
      setSentiment(response.data.sentiment_analysis);
      toast.success("Respuesta generada");
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al generar respuesta";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!newConfig.name || !newConfig.keywords || !newConfig.template) {
      toast.error("Completa todos los campos");
      return;
    }

    try {
      await axios.post(`${API}/auto-response/configs`, {
        name: newConfig.name,
        trigger_keywords: newConfig.keywords.split(',').map(k => k.trim()),
        response_template: newConfig.template,
        is_active: true,
        priority: newConfig.priority
      });
      toast.success("Configuracion guardada");
      setNewConfig({ name: "", keywords: "", template: "", priority: 0 });
      fetchConfigs();
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const deleteConfig = async (id) => {
    try {
      await axios.delete(`${API}/auto-response/configs/${id}`);
      toast.success("Eliminado");
      fetchConfigs();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="auto-responses-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Auto-Respuestas</h1>
        <p className="text-muted-foreground mt-2">Respuestas inteligentes con analisis de sentimiento</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Generador de Respuestas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea placeholder="Ej: Hola, me gustaria saber el horario de atencion..." value={message} onChange={(e) => setMessage(e.target.value)} rows={3} />
            <Button className="w-full btn-gradient" onClick={generateResponse} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Generar Respuesta
            </Button>

            {sentiment && (
              <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                <p className="text-sm font-medium">Analisis de Sentimiento</p>
                <div className="flex items-center gap-2">
                  <Badge className={sentiment.sentiment === 'positive' ? 'bg-green-500' : sentiment.sentiment === 'negative' ? 'bg-red-500' : 'bg-gray-500'}>{sentiment.sentiment}</Badge>
                  <Badge variant="outline">{sentiment.urgency} urgencia</Badge>
                </div>
                {sentiment.emotions?.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {sentiment.emotions.map((e, i) => (<Badge key={i} variant="secondary" className="text-xs">{e}</Badge>))}
                  </div>
                )}
              </div>
            )}

            {generatedResponse && (
              <div className="ai-output">{generatedResponse}</div>
            )}

            {error && !generatedResponse && (
              <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertCircle className="w-5 h-5" />
                  <p className="font-medium">Error al generar respuesta</p>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{error}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Configuraciones</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
              <Input placeholder="Nombre" value={newConfig.name} onChange={(e) => setNewConfig({...newConfig, name: e.target.value})} />
              <Input placeholder="Keywords (separadas por coma)" value={newConfig.keywords} onChange={(e) => setNewConfig({...newConfig, keywords: e.target.value})} />
              <Textarea placeholder="Plantilla de respuesta..." value={newConfig.template} onChange={(e) => setNewConfig({...newConfig, template: e.target.value})} rows={2} />
              <div className="flex items-center gap-2">
                <Label>Prioridad: {newConfig.priority}</Label>
                <input type="range" min="0" max="10" value={newConfig.priority} onChange={(e) => setNewConfig({...newConfig, priority: parseInt(e.target.value)})} className="flex-1" />
              </div>
              <Button onClick={saveConfig} className="w-full"><Plus className="w-4 h-4 mr-2" />Agregar</Button>
            </div>

            <ScrollArea className="h-64">
              <div className="space-y-2">
                {configs.map((config) => (
                  <div key={config.id} className="flex items-start justify-between p-3 bg-muted/20 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{config.name}</p>
                        <Badge variant={config.is_active ? "default" : "secondary"} className="text-xs">{config.is_active ? "Activo" : "Inactivo"}</Badge>
                        <Badge variant="outline" className="text-xs">P:{config.priority || 0}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {config.trigger_keywords?.map((kw, i) => (<Badge key={i} variant="outline" className="text-xs">{kw}</Badge>))}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deleteConfig(config.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Analytics Page
const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/overview?days=${days}`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Error al cargar analytics");
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (type) => {
    try {
      const response = await axios.get(`${API}/export/${type}?format=csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exportado correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al exportar");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-2">Metricas detalladas de tu marketing</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => exportData('content')}><Download className="w-4 h-4 mr-2" />Exportar</Button>
        </div>
      </div>

      {analytics && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Contenido" value={analytics.content?.total_generated || 0} icon={FileText} color="from-violet-600 to-indigo-600" />
            <StatCard label="SEO" value={analytics.seo?.total_analyses || 0} icon={Search} color="from-emerald-600 to-teal-600" />
            <StatCard label="Leads" value={analytics.leads?.total || 0} icon={Users} color="from-pink-600 to-rose-600" />
            <StatCard label="Campanas" value={analytics.campaigns?.total || 0} icon={Megaphone} color="from-cyan-600 to-blue-600" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Contenido por Tipo</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(analytics.content?.by_type || {}).map(([type, count]) => (
                    <div key={type} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="capitalize">{type.replace('_', ' ')}</span>
                        <span>{count}</span>
                      </div>
                      <Progress value={(count / (analytics.content?.total_generated || 1)) * 100} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Leads por Estado</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(analytics.leads?.by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                      <span className="capitalize">{status}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

// Social Media Publisher Page
const SocialPublisher = () => {
  const [content, setContent] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [hashtags, setHashtags] = useState("");
  const [link, setLink] = useState("");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [isScheduled, setIsScheduled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [queue, setQueue] = useState({ upcoming: [], recently_published: [], failed: [] });
  const [platforms, setPlatforms] = useState({});
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectPlatform, setConnectPlatform] = useState(null);
  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountToken, setNewAccountToken] = useState("");

  useEffect(() => {
    fetchAccounts();
    fetchQueue();
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/social/platforms`);
      setPlatforms(response.data.platforms || {});
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/social/accounts`);
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchQueue = async () => {
    try {
      const response = await axios.get(`${API}/social/queue`);
      setQueue(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const connectAccount = async () => {
    if (!connectPlatform || !newAccountName) {
      toast.error("Completa los campos");
      return;
    }

    try {
      await axios.post(`${API}/social/accounts`, {
        platform: connectPlatform,
        account_name: newAccountName,
        access_token: newAccountToken || null,
        is_connected: !!newAccountToken
      });
      
      toast.success(`Cuenta de ${connectPlatform} agregada`);
      setShowConnectModal(false);
      setConnectPlatform(null);
      setNewAccountName("");
      setNewAccountToken("");
      fetchAccounts();
    } catch (error) {
      toast.error("Error al conectar cuenta");
    }
  };

  const disconnectAccount = async (accountId) => {
    try {
      await axios.delete(`${API}/social/accounts/${accountId}`);
      toast.success("Cuenta desconectada");
      fetchAccounts();
    } catch (error) {
      toast.error("Error al desconectar");
    }
  };

  const publishPost = async () => {
    if (!content.trim()) {
      toast.error("Escribe contenido para publicar");
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error("Selecciona al menos una plataforma");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        content,
        platforms: selectedPlatforms,
        hashtags: hashtags ? hashtags.split(' ').filter(h => h.startsWith('#')) : [],
        link: link || null,
        schedule_time: isScheduled && scheduleDate && scheduleTime 
          ? new Date(`${scheduleDate}T${scheduleTime}`).toISOString()
          : null
      };

      const response = await axios.post(`${API}/social/posts/publish`, payload);
      
      if (response.data.status === "scheduled") {
        toast.success(`Post programado para ${scheduleDate} ${scheduleTime}`);
      } else {
        const results = response.data.results || {};
        const successCount = Object.values(results).filter(r => r.success).length;
        const pendingCount = Object.values(results).filter(r => r.setup_required || r.token_status === "configured").length;
        
        if (successCount > 0) {
          toast.success(`Publicado en ${successCount} plataformas`);
        } else if (pendingCount > 0) {
          toast.info("Post guardado. Configura los tokens de las APIs para publicar.");
        } else {
          toast.warning("Post guardado pero las cuentas requieren configuracion");
        }
      }

      setContent("");
      setHashtags("");
      setLink("");
      setSelectedPlatforms([]);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al publicar");
    } finally {
      setLoading(false);
    }
  };

  const cancelPost = async (postId) => {
    try {
      await axios.delete(`${API}/social/posts/${postId}`);
      toast.success("Post cancelado");
      fetchQueue();
    } catch (error) {
      toast.error("Error al cancelar");
    }
  };

  const retryPost = async (postId) => {
    try {
      await axios.post(`${API}/social/posts/${postId}/retry`);
      toast.success("Reintentando publicacion...");
      fetchQueue();
    } catch (error) {
      toast.error("Error al reintentar");
    }
  };

  const platformIcons = {
    instagram: Instagram,
    facebook: Facebook,
    twitter: Twitter,
    linkedin: Linkedin,
    tiktok: Video
  };

  const platformColors = {
    instagram: "from-pink-500 to-purple-600",
    facebook: "from-blue-600 to-blue-700",
    twitter: "from-sky-400 to-sky-600",
    linkedin: "from-blue-700 to-blue-800",
    tiktok: "from-gray-900 to-gray-800"
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="social-publisher-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Social Publisher</h1>
        <p className="text-muted-foreground mt-2">Publica y programa contenido en redes sociales</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Composer */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Crear Publicacion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Contenido</Label>
              <Textarea
                placeholder="Escribe tu publicacion aqui..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={5}
                className="resize-none"
                data-testid="post-content"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{content.length} caracteres</span>
                <span>Twitter: {280 - content.length} restantes</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Plataformas</Label>
              <div className="flex flex-wrap gap-2">
                {Object.keys(platforms).map((platform) => {
                  const Icon = platformIcons[platform] || Globe;
                  const isConnected = accounts.some(a => a.platform === platform && a.is_connected);
                  const isSelected = selectedPlatforms.includes(platform);
                  
                  return (
                    <Button
                      key={platform}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => togglePlatform(platform)}
                      className={`gap-2 ${isSelected ? `bg-gradient-to-r ${platformColors[platform]}` : ''}`}
                      data-testid={`platform-btn-${platform}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="capitalize">{platform}</span>
                      {isConnected ? (
                        <Check className="w-3 h-3 text-green-400" />
                      ) : (
                        <span className="text-[10px] opacity-70">!</span>
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hashtags</Label>
                <Input
                  placeholder="#marketing #socialmedia"
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  data-testid="post-hashtags"
                />
              </div>
              <div className="space-y-2">
                <Label>Link (opcional)</Label>
                <Input
                  placeholder="https://..."
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  data-testid="post-link"
                />
              </div>
            </div>

            <Separator />

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={isScheduled}
                  onCheckedChange={setIsScheduled}
                  data-testid="schedule-toggle"
                />
                <Label>Programar</Label>
              </div>
              
              {isScheduled && (
                <div className="flex gap-2 flex-1">
                  <Input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="flex-1"
                    data-testid="schedule-date"
                  />
                  <Input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-32"
                    data-testid="schedule-time"
                  />
                </div>
              )}
            </div>

            <Button
              className="w-full btn-gradient"
              onClick={publishPost}
              disabled={loading || !content.trim() || selectedPlatforms.length === 0}
              data-testid="btn-publish"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
              ) : isScheduled ? (
                <><Calendar className="w-4 h-4 mr-2" />Programar Publicacion</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Publicar Ahora</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Connected Accounts */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Cuentas Conectadas</CardTitle>
                <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline"><Plus className="w-4 h-4" /></Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Conectar Red Social</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Plataforma</Label>
                        <Select value={connectPlatform || ""} onValueChange={setConnectPlatform}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(platforms).map(([key, value]) => (
                              <SelectItem key={key} value={key}>{value.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Nombre de cuenta</Label>
                        <Input
                          placeholder="@tucuenta"
                          value={newAccountName}
                          onChange={(e) => setNewAccountName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Access Token (opcional)</Label>
                        <Input
                          placeholder="Token de la API..."
                          value={newAccountToken}
                          onChange={(e) => setNewAccountToken(e.target.value)}
                          type="password"
                        />
                        <p className="text-xs text-muted-foreground">
                          Puedes agregar el token despues desde la configuracion de la plataforma
                        </p>
                      </div>
                      <Button className="w-full" onClick={connectAccount}>
                        <Plus className="w-4 h-4 mr-2" />Agregar Cuenta
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {accounts.length > 0 ? (
                <div className="space-y-2">
                  {accounts.map((account) => {
                    const Icon = platformIcons[account.platform] || Globe;
                    return (
                      <div key={account.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${platformColors[account.platform] || 'from-gray-600 to-gray-700'} flex items-center justify-center`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{account.account_name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{account.platform}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={account.is_connected ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}>
                            {account.is_connected ? 'Conectado' : 'Pendiente'}
                          </Badge>
                          <Button variant="ghost" size="icon" onClick={() => disconnectAccount(account.id)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay cuentas conectadas</p>
                  <Button variant="link" size="sm" onClick={() => setShowConnectModal(true)}>
                    Conectar primera cuenta
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Cola de Publicacion
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Programados</span>
                  <Badge variant="secondary">{queue.queue_stats?.pending || 0}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Publicados hoy</span>
                  <Badge className="bg-green-500">{queue.queue_stats?.published_today || 0}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Fallidos</span>
                  <Badge variant="destructive">{queue.queue_stats?.failed_count || 0}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Queue Lists */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              Programados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {queue.upcoming?.length > 0 ? (
                <div className="space-y-2">
                  {queue.upcoming.map((post) => (
                    <div key={post.id} className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-sm line-clamp-2">{post.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex gap-1">
                          {post.platforms?.map(p => {
                            const Icon = platformIcons[p] || Globe;
                            return <Icon key={p} className="w-3 h-3 text-muted-foreground" />;
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {new Date(post.scheduled_time).toLocaleDateString()}
                          </span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => cancelPost(post.id)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8 text-sm">Sin posts programados</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Published */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              Publicados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {queue.recently_published?.length > 0 ? (
                <div className="space-y-2">
                  {queue.recently_published.map((post) => (
                    <div key={post.id} className="p-3 bg-green-500/10 rounded-lg">
                      <p className="text-sm line-clamp-2">{post.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex gap-1">
                          {post.platforms?.map(p => {
                            const Icon = platformIcons[p] || Globe;
                            return <Icon key={p} className="w-3 h-3 text-green-500" />;
                          })}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {post.published_at ? new Date(post.published_at).toLocaleDateString() : '-'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8 text-sm">Sin publicaciones recientes</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Failed */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <X className="w-4 h-4 text-red-500" />
              Fallidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {queue.failed?.length > 0 ? (
                <div className="space-y-2">
                  {queue.failed.map((post) => (
                    <div key={post.id} className="p-3 bg-red-500/10 rounded-lg">
                      <p className="text-sm line-clamp-2">{post.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex gap-1">
                          {post.platforms?.map(p => {
                            const Icon = platformIcons[p] || Globe;
                            return <Icon key={p} className="w-3 h-3 text-red-500" />;
                          })}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => retryPost(post.id)}>
                          <RefreshCw className="w-3 h-3 mr-1" />Reintentar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8 text-sm">Sin errores</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


// Settings Page
const SettingsPage = () => {
  const [chatwootStatus, setChatwootStatus] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [userLoading, setUserLoading] = useState(false);
  const [contactScanResult, setContactScanResult] = useState(null);
  const [contactScanLoading, setContactScanLoading] = useState(false);
  const [contactDeleteLoading, setContactDeleteLoading] = useState(false);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    checkStatus();
    if (currentUser?.is_admin) fetchUsers();
  }, [currentUser]);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/chatwoot/status`);
      setChatwootStatus(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const scanEmailOnlyContacts = async () => {
    setContactScanLoading(true);
    setContactScanResult(null);
    try {
      const res = await axios.get(`${API}/chatwoot/contacts/email-only-count`);
      setContactScanResult(res.data);
    } catch (e) {
      toast.error('Error al escanear contactos');
    } finally {
      setContactScanLoading(false);
    }
  };

  const deleteEmailOnlyContacts = async () => {
    if (!contactScanResult?.email_only_count) return;
    setContactDeleteLoading(true);
    try {
      const res = await axios.post(`${API}/chatwoot/contacts/bulk-delete-email-only`);
      toast.success(`${res.data.total_deleted} contactos eliminados correctamente`);
      setContactScanResult(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar contactos');
    } finally {
      setContactDeleteLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`);
      setUsersList(res.data);
    } catch (e) { console.error(e); }
  };

  const createUser = async () => {
    if (!newUsername || !newPassword) { toast.error('Usuario y contraseña son obligatorios'); return; }
    setUserLoading(true);
    try {
      await axios.post(`${API}/auth/users`, { username: newUsername, password: newPassword, email: newEmail || undefined, is_admin: true });
      toast.success('Usuario creado correctamente');
      setNewUsername(''); setNewPassword(''); setNewEmail('');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear usuario');
    } finally { setUserLoading(false); }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/auth/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">Configuracion</h1>
        <p className="text-muted-foreground mt-2">Gestiona integraciones y ajustes</p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" />Chatwoot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${chatwootStatus?.configured ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                <div>
                  <p className="font-medium">Estado</p>
                  <p className="text-sm text-muted-foreground">{chatwootStatus?.configured ? 'Conectado' : 'No configurado'}</p>
                </div>
              </div>
              {chatwootStatus?.url && (
                <a href={chatwootStatus.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                  Abrir<ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="font-medium mb-2">Webhook URL</p>
              <code className="text-sm bg-background p-2 rounded block break-all">{`${BACKEND_URL}/api/chatwoot/webhook`}</code>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div>
                <p className="font-medium">Limpieza de contactos mal importados</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Detecta y elimina contactos que solo tienen email (sin nombre ni teléfono), típico de importaciones masivas desde n8n u otras herramientas.
                </p>
              </div>
              {contactScanResult && (
                <div className={`flex items-center gap-3 p-3 rounded-lg border ${contactScanResult.email_only_count > 0 ? 'border-orange-500/40 bg-orange-500/10' : 'border-green-500/40 bg-green-500/10'}`}>
                  {contactScanResult.email_only_count > 0 ? (
                    <>
                      <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0" />
                      <span className="text-sm font-medium">Se encontraron <strong>{contactScanResult.email_only_count}</strong> contactos sin nombre ni teléfono</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 text-green-500 shrink-0" />
                      <span className="text-sm font-medium">No hay contactos problemáticos</span>
                    </>
                  )}
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={scanEmailOnlyContacts} disabled={contactScanLoading}>
                  {contactScanLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Escaneando...</> : <><Search className="w-4 h-4 mr-2" />Escanear contactos</>}
                </Button>
                {contactScanResult?.email_only_count > 0 && (
                  <Button variant="destructive" size="sm" onClick={deleteEmailOnlyContacts} disabled={contactDeleteLoading}>
                    {contactDeleteLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Eliminando...</> : <><Trash2 className="w-4 h-4 mr-2" />Eliminar {contactScanResult.email_only_count} contactos</>}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />IA</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium">Zhipu AI GLM-5</p>
                  <p className="text-sm text-muted-foreground">744B MoE Model</p>
                </div>
              </div>
              <Badge className="bg-green-500/20 text-green-500 border-green-500/50">Activo</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5 text-primary" />Funcionalidades</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                "Generacion de Contenido", "A/B Testing", "Repurpose", "Hashtags",
                "SEO Avanzado", "Calendario", "Templates", "Lead Scoring",
                "Campanas", "Email Sequences", "Sentiment Analysis", "Analytics"
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {currentUser?.is_admin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />Gestión de Usuarios
              </CardTitle>
              <CardDescription>Crea y administra accesos a MarketAI Pro</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-3 p-4 border border-border rounded-lg">
                <p className="text-sm font-medium flex items-center gap-2"><Plus className="w-4 h-4 text-primary" />Nuevo usuario</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Input placeholder="Nombre de usuario" value={newUsername} onChange={e => setNewUsername(e.target.value)} />
                  <Input type="password" placeholder="Contraseña" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                  <Input type="email" placeholder="Email (opcional)" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
                </div>
                <Button onClick={createUser} disabled={userLoading} size="sm">
                  {userLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Crear usuario
                </Button>
              </div>

              {usersList.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Usuarios ({usersList.length})</p>
                  {usersList.map(u => (
                    <div key={u.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-xs font-bold text-primary">{u.username.charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">{u.username}</p>
                          {u.email && <p className="text-xs text-muted-foreground">{u.email}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {u.is_admin && <Badge variant="outline" className="text-xs">Admin</Badge>}
                        {u.id !== currentUser?.sub && (
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10 w-8 h-8" onClick={() => deleteUser(u.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============== RESPONSE FORMS ==============
const ResponseFormsPage = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/response-forms?limit=${LIMIT}&offset=${page * LIMIT}`;
      if (filter === "pending") url += "&procesado=false";
      else if (filter === "processed") url += "&procesado=true";
      const res = await axios.get(url);
      setForms(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Error al cargar formularios");
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const toggleProcesado = async (id, current) => {
    try {
      await axios.patch(`${API}/response-forms/${id}?procesado=${!current}`);
      toast.success("Estado actualizado");
      fetchForms();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const deleteForm = async (id) => {
    if (!window.confirm("¿Eliminar esta respuesta?")) return;
    try {
      await axios.delete(`${API}/response-forms/${id}`);
      toast.success("Respuesta eliminada");
      fetchForms();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Formularios</h1>
          <p className="text-muted-foreground mt-1">{total} respuestas recibidas</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={(v) => { setFilter(v); setPage(0); }}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="processed">Procesados</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchForms}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : forms.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No hay formularios</p>
              <p className="text-sm mt-1">Las respuestas aparecerán aquí cuando los usuarios envíen el formulario.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Nombre</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Empresa</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Cargo</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Puesto</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Fecha</th>
                      <th className="text-center px-4 py-3 font-medium text-muted-foreground">Estado</th>
                      <th className="text-center px-4 py-3 font-medium text-muted-foreground w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {forms.map((f) => (
                      <tr key={f.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 font-medium">{f.nombre}</td>
                        <td className="px-4 py-3 text-muted-foreground">{f.email}</td>
                        <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{f.empresa || "—"}</td>
                        <td className="px-4 py-3 hidden lg:table-cell"><Badge variant="outline">{f.cargo || "—"}</Badge></td>
                        <td className="px-4 py-3 hidden lg:table-cell"><Badge variant="secondary">{f.puesto || "—"}</Badge></td>
                        <td className="px-4 py-3 text-muted-foreground text-xs hidden sm:table-cell">{f.fecha_registro ? new Date(f.fecha_registro).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant={f.procesado ? "default" : "outline"}
                            size="sm"
                            className="text-xs"
                            onClick={() => toggleProcesado(f.id, f.procesado)}
                          >
                            {f.procesado ? <><Check className="w-3 h-3 mr-1" /> Procesado</> : <><Clock className="w-3 h-3 mr-1" /> Pendiente</>}
                          </Button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => deleteForm(f.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-muted-foreground">Página {page + 1} de {totalPages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Main Layout
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <Header setIsOpen={setSidebarOpen} />
      <main className="lg:ml-64 pt-8 pb-12 px-6 lg:px-10">{children}</main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

const AppRoutes = () => {
  const { user } = useAuth();

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/content" element={<ContentGenerator />} />
        <Route path="/ab-testing" element={<ABTestingPage />} />
        <Route path="/repurpose" element={<RepurposePage />} />
        <Route path="/hashtags" element={<HashtagsPage />} />
        <Route path="/images" element={<ImageGenerator />} />
        <Route path="/seo" element={<SEOAssistant />} />
        <Route path="/social-publisher" element={<SocialPublisher />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/email-sequences" element={<EmailSequencesPage />} />
        <Route path="/auto-responses" element={<AutoResponses />} />
        <Route path="/response-forms" element={<ResponseFormsPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

export default App;
